import FilterListIcon from '@mui/icons-material/FilterList';
import { Box, SxProps } from '@mui/material';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { Customer } from '../../../services/entities/impl/blfApi/schemas/customer';
import {
  CropType,
  CropTypes,
} from '../../../services/entities/impl/blfApi/schemas/variety';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import CheckboxAndLabel from '../../commonUI/CheckboxAndLabel';
import { ReduxSearchBar } from '../../commonUI/ReduxSearchBar';
import { flexCenter } from '../../commonUI/cssClasses';
import BaseDropdown from '../../select/BaseDropdown';
import LoadingSpinnerWrapper from '../../select/LoadingSpinnerWrapper';
import { useOnboardContext } from '../context/OnboardContext';
import { toSelectOptions } from '../forms/generic/crud/utils/toSelectOptions';
import {
  OnboardingTabsEnum,
  selectCurrOnboardingTab,
  selectFilterCropType,
  selectFilterCustomerID,
  selectFilterRanchID,
  selectInvalidItemsCheckbox,
  selectOnboardSearch,
  setFilterCropType,
  setFilterCustomerID,
  setFilterRanchID,
  setInvalidItemsCheckbox,
  setOnboardSearch,
} from '../onboardingSlice';
const commonFilterRowStyles: SxProps = {
  width: 'min-content',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  overflow: 'hidden',
  // mb: '0.5rem',
};

const commonFilterIconStyles: SxProps = {
  mr: '0.5rem',
  mt: '1px',
};

const commonDropdownStyles: SxProps = {
  fontSize: '1rem',
};

const OnboardListFilters: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const filterCustomerID = useAppSelector(selectFilterCustomerID);
  const filterRanchID = useAppSelector(selectFilterRanchID);
  const filterCropType = useAppSelector(selectFilterCropType);
  const currOnboardingTab = useAppSelector(selectCurrOnboardingTab);
  const invalidItemsCheckbox = useAppSelector(selectInvalidItemsCheckbox);
  const { ApiDataRaw, ApiDataFiltered } = useOnboardContext();

  const handleFilterCustomerChange = (newID: string) => {
    dispatch(setFilterCustomerID(newID));
    dispatch(setFilterRanchID(''));
  };

  const handleFilterRanchChange = (newID: string) => {
    dispatch(setFilterRanchID(newID));
  };

  const handleFilterCropTypeChange = (newCropType: CropType) => {
    dispatch(setFilterCropType(newCropType));
  };

  // if there is only one customer, set it as the filter
  useEffect(() => {
    if (ApiDataFiltered.customers?.length === 1) {
      dispatch(setFilterCustomerID(ApiDataFiltered.customers[0].id));
    }
  }, [ApiDataFiltered.customers, dispatch]);

  // Conditionally show or hide the filters
  const showCustomerFilter =
    ApiDataFiltered.customers && ApiDataFiltered.customers?.length >= 1;

  const showRanchFilter = currOnboardingTab === OnboardingTabsEnum.blocks;
  const showCropTypeFilter = currOnboardingTab === OnboardingTabsEnum.blocks;

  // If ranch filter is hidden, reset it
  useEffect(() => {
    if (!showRanchFilter) {
      dispatch(setFilterRanchID(''));
    }
  }, [dispatch, showRanchFilter]);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        mt: '0.5rem',
      }}
    >
      {showCustomerFilter && (
        <Box sx={{ ...commonFilterRowStyles }}>
          <FilterListIcon sx={{ ...commonFilterIconStyles }} fontSize='small' />
          <BaseDropdown
            value={filterCustomerID}
            items={ApiDataRaw.customers || []}
            label={`${t('All', { ns: 'common' })} ${t('customers', {
              ns: 'common',
            })}`}
            onChange={(newID) => {
              handleFilterCustomerChange(newID);
            }}
            optionValueGetter={(customer: Customer) => customer.id}
            optionLabelGetter={(customer: Customer) => customer.name}
            optionKeyGetter={(customer: Customer) => customer.id}
            sx={{ ...commonDropdownStyles }}
          />
          <LoadingSpinnerWrapper isLoading={ApiDataRaw.isLoadingCustomers} />
        </Box>
      )}
      {showRanchFilter && (
        <Box sx={{ ...commonFilterRowStyles }}>
          <FilterListIcon sx={{ ...commonFilterIconStyles }} fontSize='small' />
          <BaseDropdown
            value={filterRanchID}
            items={ApiDataFiltered.ranches || []}
            label={`${t('All', { ns: 'common' })} ${t('ranches', {
              ns: 'common',
            })}`}
            onChange={(newID) => {
              handleFilterRanchChange(newID);
            }}
            optionValueGetter={(ranch) => ranch.id}
            optionLabelGetter={(ranch) => ranch.name}
            optionKeyGetter={(ranch) => ranch.id}
            sx={{ ...commonDropdownStyles }}
          />
          <LoadingSpinnerWrapper isLoading={ApiDataRaw.isLoadingRanches} />
        </Box>
      )}
      {showCropTypeFilter && (
        <Box sx={{ ...commonFilterRowStyles }}>
          <FilterListIcon sx={{ ...commonFilterIconStyles }} fontSize='small' />
          <BaseDropdown
            value={filterCropType ?? ''}
            items={toSelectOptions(CropTypes)}
            label={`${t('All Crop Types', { ns: 'common' })}`}
            onChange={(newCropType) => {
              handleFilterCropTypeChange(newCropType);
            }}
            optionValueGetter={(cropType) => cropType.value}
            optionLabelGetter={(cropType) => cropType.label}
            optionKeyGetter={(cropType) => cropType.value}
            sx={{ ...commonDropdownStyles }}
          />
          <LoadingSpinnerWrapper isLoading={ApiDataRaw.isLoadingRanches} />
        </Box>
      )}
      <Box sx={{ ...commonFilterRowStyles }}>
        <CheckboxAndLabel
          label={t('Invalid Only', { ns: 'common' })}
          handleChange={() =>
            dispatch(setInvalidItemsCheckbox(!invalidItemsCheckbox))
          }
          checked={invalidItemsCheckbox}
        />
      </Box>
      <Box sx={{ width: '100%', ...flexCenter, mb: '0.5rem' }}>
        <ReduxSearchBar
          selectReduxItem={selectOnboardSearch}
          updateRedux={setOnboardSearch}
        />
      </Box>
    </Box>
  );
};

export default OnboardListFilters;
